import type { JumpEngineConfig, JumpHazard } from './state.js';

/**
 * Float-safety clearance the planner targets ABOVE a hazard's hard edge, so
 * accumulated floating-point drift in the engine's parabolic integration
 * never nudges the cube's bottom across the lethal line by a fraction of a
 * pixel. The engine uses a strict `>` collision test, so a value exactly on
 * the edge wouldn't collide if the math were exact — this margin keeps every
 * planned jump solidly clear. The chart-builder filter shares the same module
 * so "filter accepts an obstacle" ⇔ "planner clears it."
 */
export const JUMP_PLANNER_EDGE_MARGIN = 1.5;

/**
 * The contiguous range of press beats `[earliest, latest]` from which a jump
 * launched off a surface at `launchTopY` clears the hazard at `hazard.beat`,
 * plus the apex-aligned press that maximises clearance. The window exists
 * because the jump arc is an upward parabola in flight time: the cube sits
 * below the clear line for a contiguous band of `(hazardBeat − pressBeat)`
 * values centred on the apex lead.
 */
export interface JumpFeasibleWindow {
  /** Earliest press beat that still clears (apex lands AFTER the hazard). */
  earliest: number;
  /** Latest press beat that still clears (apex lands BEFORE the hazard). */
  latest: number;
  /** Press beat whose apex sits exactly over the hazard — maximum clearance. */
  apexAligned: number;
}

/**
 * Analytic feasible press window for clearing `hazard` with a single jump off
 * a surface whose top is `launchTopY`, launched at speed `v0`. Returns null
 * when the jump physically cannot reach above the hazard (its tip is taller
 * than the peak height from that surface at `v0`). Used to SEED the engine-
 * backed planner's candidate presses; the planner then verifies each candidate
 * through the real `step()` so surface/elevation effects are exact.
 *
 * `v0` is the launch speed of the jump being planned: the full
 * {@link JumpEngineConfig.jumpVelocity} for a long jump, or
 * {@link JumpEngineConfig.shortJumpVelocity} for a short (tapped) jump — the
 * planner tries both so a hazard under a ceiling (long jump blocked) is still
 * cleared by a short jump when one fits.
 *
 * The hazard is a point at `hazard.beat`: the press band is the contiguous set
 * of launch beats whose arc sits above the clear line there, centred on the
 * apex-aligned press. Adjacent cluster columns are planned independently — the
 * arc that clears one clears the rest it spans.
 */
export function feasibleJumpWindow(
  config: JumpEngineConfig,
  hazard: JumpHazard,
  launchTopY: number,
  v0: number,
): JumpFeasibleWindow | null {
  const g = config.gravity;
  const half = config.playerHalf;
  const launchCentre = launchTopY - half;
  // Clear iff cube centre at the hazard's beat <= topY − half − margin.
  const yTarget = hazard.topY - half - JUMP_PLANNER_EDGE_MARGIN;
  // f(dt) = launchCentre − v0·dt + ½·g·dt² <= yTarget, dt = hazardBeat − pressBeat.
  // ½·g·dt² − v0·dt + (launchCentre − yTarget) = 0.
  const disc = v0 * v0 - 2 * g * (launchCentre - yTarget);
  if (disc < 0) {
    return null;
  }
  const root = Math.sqrt(disc);
  const dtMin = (v0 - root) / g;
  const dtMax = (v0 + root) / g;
  const apexDt = v0 / g;
  return {
    // Earliest press still clears (apex lands latest).
    earliest: hazard.beat - dtMax,
    // Latest press still clears (apex lands earliest).
    latest: hazard.beat - dtMin,
    // Apex over the hazard — maximum clearance.
    apexAligned: hazard.beat - apexDt,
  };
}

/**
 * Candidate press beats to try for `hazard` at launch speed `v0`, ordered
 * best-first (apex-aligned = maximum clearance first, then a fan across the
 * feasible window). Clamped to `>= notBeforeBeat` (the cube can't press before
 * it has landed / before the prior committed beat). Returns an empty list when
 * the hazard is unreachable from `launchTopY` at `v0`.
 */
export function jumpPressCandidates(
  config: JumpEngineConfig,
  hazard: JumpHazard,
  launchTopY: number,
  notBeforeBeat: number,
  v0: number,
  fan = 9,
): number[] {
  const window = feasibleJumpWindow(config, hazard, launchTopY, v0);
  if (!window) {
    return [];
  }
  const lo = Math.max(window.earliest, notBeforeBeat);
  const hi = Math.min(window.latest, hazard.beat);
  if (lo > hi + 1e-9) {
    return [];
  }
  const out: number[] = [];
  const apex = Math.min(Math.max(window.apexAligned, lo), hi);
  out.push(apex);
  for (let i = 0; i <= fan; i += 1) {
    const j = lo + ((hi - lo) * i) / fan;
    if (Math.abs(j - apex) > 1e-6) {
      out.push(j);
    }
  }
  return out;
}
